import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, ArrowRight, Info, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import {
  announcementsService,
  AnnouncementType,
  isAnnouncementExpired,
  normalizeAnnouncementText,
} from '../services/announcementsService';
import { formatDate } from '../utils/helpers';

const TYPE_ICON: Record<AnnouncementType, React.ComponentType<{ className?: string }>> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

const TYPE_COLOR: Record<AnnouncementType, string> = {
  info: 'text-blue-600',
  success: 'text-green-600',
  warning: 'text-orange-600',
  error: 'text-red-600',
};

/** Kolik posledních novinek se vejde do náhledu na přehledu. */
const NAHLED = 4;

/**
 * Náhled posledních novinek na přehledu. Na rozdíl od pruhu nahoře
 * (AnnouncementsBanner) je vidět VŽDY a ukazuje i novinky, kterým už skončila
 * platnost – proto se dá ke sdělení vrátit, i když ho mezitím někdo odbyl
 * nebo mu vypršela platnost. Prokliknutím se otevře celý přehled.
 */
export const NewsPreviewCard: React.FC<{ limit?: number }> = ({ limit = NAHLED }) => {
  const navigate = useNavigate();

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['announcements', 'for-me'],
    queryFn: () => announcementsService.getMyAnnouncements(),
  });

  const zobrazene = announcements.slice(0, limit);
  const neprectenych = announcements.filter((a) => !a.isRead).length;

  return (
    <div className="bg-white rounded-lg shadow border-2 border-gray-300">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h2 className="flex items-center text-lg font-semibold text-gray-900">
          <Bell className="w-5 h-5 mr-2 text-gray-400" />
          Novinky
          {neprectenych > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-blue-600 text-white text-xs font-semibold">
              {neprectenych} nové
            </span>
          )}
        </h2>
        <button
          onClick={() => navigate('/news')}
          className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          Zobrazit všechny
          {announcements.length > 0 ? ` (${announcements.length})` : ''}
          <ArrowRight className="w-4 h-4 ml-1" />
        </button>
      </div>

      {isLoading ? (
        <div className="px-6 py-8 text-center text-sm text-gray-500">Načítám…</div>
      ) : zobrazene.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-gray-500">Zatím tu žádné novinky nejsou.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {zobrazene.map((a) => {
            const Icon = TYPE_ICON[a.type];
            const expired = isAnnouncementExpired(a);
            return (
              <li key={a.id}>
                <button
                  onClick={() => navigate('/news')}
                  className="w-full text-left px-6 py-3 hover:bg-gray-50 flex items-start gap-3"
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${expired ? 'text-gray-300' : TYPE_COLOR[a.type]}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`text-sm truncate ${expired ? 'text-gray-500' : 'text-gray-900'} ${
                          a.isRead ? 'font-normal' : 'font-semibold'
                        }`}
                      >
                        {a.title}
                      </span>
                      {expired && (
                        <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">
                          už neplatí
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{normalizeAnnouncementText(a.content)}</p>
                  </div>
                  <span className="flex-shrink-0 text-xs text-gray-400">{formatDate(a.createdAt)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
